import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { useParams } from "react-router-dom";
import {Block, formatDateDDMM, getWeekdayShort} from "../../../constants";
import ErrorMessage from "../../../components/ErrorMessage";
import useSubmitLock from "../../../hooks/useSubmitLock";
import TournamentBlockToggle from "../TournamentBlockToggle";

const TournamentScheduleSettings = ({ tournamentId, tournamentBlocks, setTournament, onSaved, onClose, onReset }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const { id: routeId } = useParams();
    const id = tournamentId || routeId;
    const { isSubmitting, wrapSubmit } = useSubmitLock();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [stages, setStages] = useState([]);
    // Guards fetchStages against out-of-order responses: the mount effect
    // below fires one on load, and handleReset fires another right after
    // resetting - without this, a slower pre-reset response can land after
    // the reset's and silently overwrite the freshly-reset state with the
    // old one.
    const stagesReqRef = useRef(0);

    useEffect(() => {
        fetchStages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStages = async () => {
        setLoading(true);
        stagesReqRef.current += 1;
        const currentReq = stagesReqRef.current;
        try {
            const res = await api.get(`/api/v1.0/tournaments/${id}/schedules/stages/`);
            if (stagesReqRef.current !== currentReq) return;
            setStages(res.data.results || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (stageId, field, value) => {
        setStages(prev =>
            prev.map(s => s.id === stageId ? { ...s, [field]: value } : s)
        );
    };

    const handleSave = wrapSubmit(async () => {
        const errs = {};
        stages.forEach(s => {
            if (!s.start) errs[s.id] = t("Start time is required");
            if (!s.court || Number(s.court) < 1) errs[`${s.id}_court`] = t("Court is required");
            if (!s.round || Number(s.round) < 1) errs[`${s.id}_round`] = t("Round is required");
        });
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setErrors({});

        setLoading(true);
        try {
            await Promise.all(stages.map(stage => {
                const payload = {
                    date: stage.date,
                    start: stage.start,
                    court: Number(stage.court),
                    round: Number(stage.round),
                    interval: stage.interval || "00:30:00"
                };
                return api.patch(`/api/v1.0/tournaments/${id}/schedules/stages/${stage.id}/`, payload);
            }));

            await fetchStages();
            onSaved?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    });

    const handleReset = wrapSubmit(async () => {
        if (!window.confirm(t("Reset schedule?"))) return;

        setLoading(true);
        try {
            await api.post(`/api/v1.0/tournaments/${id}/schedules/reset/`);
            await fetchStages();
            onReset ? onReset() : onSaved?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    });

    const intervalOptions = [30, 45, 60];

    const getTotalMinutes = (interval) => {
        if (!interval) return 0;
        const [h, m] = interval.split(":");
        return Number(h) * 60 + Number(m);
    };

    const formatInterval = (totalMinutes) => {
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:00`;
    };

    return (
        <form>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <TournamentBlockToggle
                    tournamentId={id}
                    tournamentBlocks={tournamentBlocks}
                    setTournament={setTournament}
                    blockType={Block.SCHEDULE}
                />
            </div>

            {stages.map(stage => (
                <div key={stage.id}>

                    <div>
                        <label>{formatDateDDMM(stage.date)} {getWeekdayShort(stage.date)}</label>
                    </div>
                    <div className="group-stage" style={{paddingBottom: 10}}>
                        <div>
                            <label>{t("Start")}</label>
                            <input
                                type="time"
                                required
                                value={stage.start || ""}
                                onChange={e => handleChange(stage.id, "start", e.target.value)}
                            />
                            {errors[stage.id] && <ErrorMessage error={errors[stage.id]} />}
                        </div>

                        <div>
                            <label>{t("Courts quantity")}</label>
                            <input
                                type="number"
                                min="1"
                                value={stage.court || ""}
                                onChange={e => handleChange(stage.id, "court", Number(e.target.value))}
                            />
                            {errors[`${stage.id}_court`] && <ErrorMessage error={errors[`${stage.id}_court`]} />}
                        </div>

                        <div>
                            <label>{t("Rounds quantity")}</label>
                            <input
                                type="number"
                                min="1"
                                value={stage.round || ""}
                                onChange={e => handleChange(stage.id, "round", Number(e.target.value))}
                            />
                            {errors[`${stage.id}_round`] && <ErrorMessage error={errors[`${stage.id}_round`]} />}
                        </div>

                        <div>
                            <label>{t("Interval")}</label>
                            <ul className="interval">
                                {intervalOptions.map(v => (
                                    <li key={v}>
                                        <label>
                                            <input
                                                type="radio"
                                                checked={getTotalMinutes(stage.interval) === v}
                                                onChange={() => handleChange(stage.id, "interval", formatInterval(v))}
                                            />
                                            {v}
                                        </label>
                                    </li>
                                ))}
                                <li>
                                    <div style={{display: "flex"}}>
                                        <label>
                                            <input
                                                type="radio"
                                                checked={!intervalOptions.includes(getTotalMinutes(stage.interval))}
                                                onChange={() =>
                                                    handleChange(stage.id, "interval", "00:00:00")
                                                }
                                            />
                                            {t("Other")}
                                        </label>
                                        <input
                                            type="number"
                                            value={
                                                !intervalOptions.includes(getTotalMinutes(stage.interval))
                                                    ? getTotalMinutes(stage.interval)
                                                    : ""
                                            }
                                            placeholder="min"
                                            onChange={e =>
                                                handleChange(stage.id, "interval", formatInterval(Number(e.target.value)))
                                            }
                                            style={{ width: "45px", marginLeft: "5px" }}
                                        />
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            ))}
            <br/>
            <ul className="tabs settings-form-actions">
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleSave}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="check" /> {t("Save")}
                    </a>
                </li>
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleReset}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="rotate-ccw" /> {t("Reset")}
                    </a>
                </li>
            </ul>
        </form>
    );
};

export default TournamentScheduleSettings;
